import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useAuthenticationInterceptor } from './useAuthenticationInterceptor';
import { useWindowFocus } from './useWindowFocus';
import API from 'api/API';
import { useRefreshFirebaseToken } from './useRefreshFirebaseToken';
import en from 'locale/en';
import routes from 'routes';
import { Auth0Error } from 'utils/helpers/auth0.helpers';
import { clearSaviAuthToken } from 'utils/saviAuthTokenStorage';
import { endSessionWrapKey, rewrapRefreshTokenIfRotated } from 'utils/helpers/pinSessionKey.helpers';
import { getLastUsedOrgId } from './useLastUsedOrgId';
import { useRememberLastLoginOrganisation } from './useRememberLastLoginOrganisation';
import { getLastUsedUserEmail } from './useLastUsedUserEmail';
import { useRememberLastLoginUser } from './useRememberLastLoginUser';
import { useQuickSwitchUsers } from 'api/hooks/useQuickSwitchUsers';
import { useClearStaleWorkstationCacheOnOrgSwitch } from './useClearStaleWorkstationCacheOnOrgSwitch';
import { useSyncPinKeyCache } from './useSyncPinKeyCache';
import { useFlushPendingPinKey } from './useFlushPendingPinKey';

export const LOGGED_OUT_REASON_KEY = 'saviLoggedOutReason';
export type LoggedOutReason = keyof typeof en.LoggedOutPage.reason;

const TOKENS_REFRESH_INTERVAL = 1000 * 60 * 15; // 15 minutes

export const useAuthorization = () => {
  const { isLoading, error, isAuthenticated, user: auth0User, logout: auth0Logout, loginWithRedirect } = useAuth0();

  // navigate to the logged-out page with reason and error and without using
  //  react-router.navigate() because the routes may not have been rendered yet
  const showLoggedOutPage = useCallback((reason: LoggedOutReason = 'login', error?: Error | Auth0Error) => {
    const pathname = `/logged-out/${reason}`;
    if (error) {
      const auth0Error = new Auth0Error(error);
      const auth0ErrorObject = JSON.parse(JSON.stringify(auth0Error));
      history.pushState(
        {
          error: auth0ErrorObject, // history.state doesnt handle Error objects well
        },
        '',
        pathname,
      );
      location.reload();
    } else if (window.location.pathname != pathname) {
      window.location.href = pathname;
    }
  }, []);

  const login = useCallback(
    async (force = false, organizationOverride?: string, loginHintOverride?: string | null) => {
      if (!force && (isAuthenticated || isLoading || error)) {
        return;
      }

      // null means explicitly no hint (a different account), so the remembered user is not used
      const noHint = loginHintOverride === null;

      // an explicit override (e.g. a direct org login link) wins; otherwise fall back to
      //  the remembered login organisation, if any, so auth0 skips the "enter your org" screen
      const organization = organizationOverride ?? getLastUsedOrgId() ?? undefined;
      // pre-fill the username/email field, e.g. when switching to a specific quick-switch user
      const loginHint = noHint ? undefined : loginHintOverride ?? getLastUsedUserEmail() ?? undefined;

      await loginWithRedirect(
        force || organization || loginHint || noHint
          ? {
              authorizationParams: {
                // force avoids an endless loop; organizationOverride forces a fresh login so
                //  auth0 doesn't silently resume the previous org's session
                ...(force || organizationOverride || noHint ? { prompt: 'login' as const } : {}),
                ...(organization ? { organization } : {}),
                ...(loginHint ? { login_hint: loginHint } : {}),
              },
            }
          : undefined,
      );
    },
    [isAuthenticated, isLoading, error, loginWithRedirect],
  );

  const logout = useCallback(
    async (reason: LoggedOutReason = 'user') => {
      sessionStorage.setItem(LOGGED_OUT_REASON_KEY, reason);
      if (auth0User?.sub) clearSaviAuthToken(auth0User.sub);
      await endSessionWrapKey();
      await auth0Logout({
        logoutParams: { returnTo: location.origin },
      });
    },
    [auth0Logout, auth0User],
  );

  // clears the local auth cache and shows the logged-out screen
  const logoutLocal = useCallback(
    async (reason: LoggedOutReason = 'login', error?: Error | Auth0Error) => {
      if (auth0User?.sub) clearSaviAuthToken(auth0User.sub);
      await endSessionWrapKey();
      await auth0Logout({ openUrl: false });
      showLoggedOutPage(reason, error);
    },
    [auth0Logout, auth0User, showLoggedOutPage],
  );

  return { login, logout, logoutLocal, showLoggedOutPage, isLoading, error, isAuthenticated };
};

export const useAuthentication = () => {
  const { isLoading, error, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const { logout, logoutLocal } = useAuthorization();

  useAuthenticationInterceptor();

  const refreshAuth0AccessToken = useCallback(async (): Promise<string | undefined> => {
    try {
      const token = await getAccessTokenSilently();
      await rewrapRefreshTokenIfRotated();
      return token;
    } catch (error) {
      API.errorHandler(error);

      logout('inactivity');
    }
  }, [getAccessTokenSilently, logout]);

  const { refreshFirebaseToken } = useRefreshFirebaseToken();

  // must run before the hooks below so it can clear out a previous org's stale preferences/cache
  //  before they're read again this render
  useClearStaleWorkstationCacheOnOrgSwitch();
  useRememberLastLoginOrganisation();
  useRememberLastLoginUser();
  // keeps the quick-switch user list cached to localStorage so the logged-out page can show it
  useQuickSwitchUsers();
  useSyncPinKeyCache();
  useFlushPendingPinKey();

  const insecureRoutes = useMemo(
    () =>
      Object.values(routes).reduce<string[]>((acc, { userAuthState, path }) => {
        if (userAuthState == 'Not Authenticated') acc.push(path.replace(/:.*/, ''));
        return acc;
      }, []),
    [],
  );

  const refreshTokens = useCallback(async () => {
    // disable for insecure routes
    // NOTE This has a caveat: Routes must be carefully named such that
    //  secure routes dont start with the same path as insecure routes.
    // If it becomes a problem we can add a check to detect mistakes.
    const isInsecureRoute = Object.values(insecureRoutes).some(route => location.pathname.startsWith(route));
    if (isInsecureRoute) return;

    if (isLoading || error) return;

    if (!isAuthenticated) {
      const savedReason = sessionStorage.getItem(LOGGED_OUT_REASON_KEY) as LoggedOutReason | null;
      if (savedReason) {
        sessionStorage.removeItem(LOGGED_OUT_REASON_KEY);
      }

      logoutLocal(savedReason || 'login');
      return;
    }

    // user is authenticated, refresh the user's tokens
    const auth0Token = await refreshAuth0AccessToken();

    await refreshFirebaseToken(auth0Token);
  }, [insecureRoutes, isLoading, error, isAuthenticated, refreshAuth0AccessToken, refreshFirebaseToken, logoutLocal]);

  // on mount/update, check if the user is authenticated
  useEffect(() => {
    refreshTokens();
  }, [refreshTokens]);

  // when the window regains focus, refresh the user's tokens
  useWindowFocus(refreshTokens);

  // on a timer, proactively refresh the user's tokens
  useEffect(() => {
    const interval = setInterval(refreshTokens, TOKENS_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [refreshAuth0AccessToken, refreshFirebaseToken, refreshTokens]);

  return { isLoading, error, isAuthenticated };
};
